import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import cx from 'classnames';
import { connect } from 'react-redux';
import { getProfile } from '@common/actions/profile';

import { Button } from '@common/components/Button';
import { MessageBox } from '@common/components/MessageBox';
import { Flex } from '@common/components/Flex';
import { isEmail } from '@common/utils/email';
import { ErrorIcon } from '@common/components/error-icon';
import { Profile } from './components/Profile';
import { Settings } from './components/Settings';
import { updateProfile, getProfileImageUrl, getProfileSettings, getModuleVisibility, updateProfileSettings } from './actions/profile';
import { PROFILE_INTERFACE_TYPE, PROFILE_MODULE_NAVID } from './const/profile';
import { getUiBridge } from '../../common/utils/ui-bridge';

import './ProfileSettings.styles.scss';

const SECTION = {
  profile: "profile",
  settings: "settings",
};

export const TABS_TYPE = {
  none: 0,
  lastSession: 1,
  custom: 2,
};

export class ProfileSettings extends React.Component {
  state = {
    currentSection: SECTION.profile,
    profile: undefined,
    settings: undefined,
    modulesVisibility: undefined,
    submitting: false,
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'medium',
      onCustomOk: () => {},
    },
  };

  componentDidMount() {
    this.getProfile();
    this.getSettings();
  }

  handleChangeSection = (e) => {
    e.persist();

    const { dataset } = e.currentTarget;

    this.setState((state) => ({ ...state, currentSection: dataset.sectionName }));
  }

  isVisible(...navids) {
    const { modulesVisibility = [] } = this.state;

    return navids.every((navid) => modulesVisibility.find((x) => x.navid === navid)?.visible);
  }

  get settingsTabs() {
    const { settings, modulesVisibility } = this.state;

    if (!settings || !modulesVisibility) {
      return [];
    }

    const { interfaceType } = settings;
    const isTestInterface = PROFILE_INTERFACE_TYPE.patients === interfaceType

    const tabs = [
      {
        label: 'Patients',
        fieldName: 'patients',
        condition: isTestInterface,
      },
      {
        label: 'Pedigrees',
        fieldName: 'pedigrees',
        condition: !isTestInterface && this.isVisible(PROFILE_MODULE_NAVID.pedigrees),
      },
      {
        label: 'Individuals',
        fieldName: 'individuals',
        condition: !isTestInterface && this.isVisible(PROFILE_MODULE_NAVID.individuals)
      },
      {
        label: 'Invite Status',
        fieldName: 'invites',
        condition: this.isVisible(PROFILE_MODULE_NAVID.invites),
      },
      {
        label: 'Edit Questionnaires',
        fieldName: 'questionnaires',
        condition: !isTestInterface && this.isVisible(PROFILE_MODULE_NAVID.invites, PROFILE_MODULE_NAVID.invitesAdmin),
      },
      {
        label: 'Email Templates',
        fieldName: 'emailTemplates',
        condition: !isTestInterface && this.isVisible(PROFILE_MODULE_NAVID.invites, PROFILE_MODULE_NAVID.invitesAdmin),
      },
      {
        label: 'Orders',
        fieldName: 'orders',
        condition: this.isVisible(PROFILE_MODULE_NAVID.orders),
      },
      {
        label: 'Letters',
        fieldName: 'letters',
        condition: this.isVisible(PROFILE_MODULE_NAVID.letters),
      },
      {
        label: 'Individual Spreadsheets',
        fieldName: 'iss',
        condition: this.isVisible(PROFILE_MODULE_NAVID.individuals, PROFILE_MODULE_NAVID.savessfmts),
      },
      {
        label: 'Pedigree Spreadsheets',
        fieldName: 'pss',
        condition: this.isVisible(PROFILE_MODULE_NAVID.pedigrees, PROFILE_MODULE_NAVID.savessfmts),
      },
    ];

    return tabs
      .filter(({ condition }) => condition)
      .map((data) => {
        return {
          ...data,
          label: I18n.t(data.label),
          value: settings.defaultTabSettings[data.fieldName]
        };
      });
  }

  get sections() {
    const { settings, profile } = this.state;

    if (!profile || !settings) {
      return undefined;
    }

    const adminUsername = 'progeny';

    return {
      [SECTION.profile]: {
        id: 'anc-profile',
        title: I18n.t('Profile'),
        titleIcon: 'fa fa-user',
        component: (
          <Profile 
            canChangePassword={profile.userName !== adminUsername}
            data={profile}
            onChange={this.handleProfileChange}
          />
        ),
      },
      [SECTION.settings]: {
        id: 'anc-settings',
        title: I18n.t('Settings'),
        titleIcon: 'fa fa-gear',
        component: (
          <Settings 
            mode={settings.mode}
            tabs={this.settingsTabs}
            onTabModeChange={this.handleTabModeChange}
            onTabsChange={this.handleTabsChange}
          />
        ),
      },
    };
  }

  getProfile = async () => {
    const { userName } = this.props;

    const profile = await getProfile(userName);

    profile.imageUrl = getProfileImageUrl(profile.imageSecParam);

    this.setState((state) => ({ ...state, profile }));
  }

  getSettings = async () => {
    const { userName } = this.props;

    const settings = await getProfileSettings(userName);
    const modulesVisibility = await getModuleVisibility(userName);

    this.setState((state) => ({ 
      ...state, 
      settings,
      modulesVisibility,
    }));
  }

  handleTabsChange = (fieldName, value) => {
    this.setState((state) => ({ 
      ...state, 
      settings: {
        ...state.settings,
        defaultTabSettings: {
          ...state.settings.defaultTabSettings,
          [fieldName]: value
        },
      } 
    }));
  }

  handleTabModeChange = (mode) => {
    this.setState((state) => ({ 
      ...state, 
      settings: {
        ...state.settings,
        mode: Number(mode),
      } 
    }));
  }

  handleProfileChange = (profile) => {
    this.setState((state) => ({ ...state, profile }));
  }

  handleSave = async () => {
    const { close } = this.props;
    const { settings, profile: { imageSecParam, imageUrl, newPasswordConfirmed, ...profileSubmitData } } = this.state;

    try {
      this.setState((state) => ({ ...state, submitting: true })); 
  
      await Promise.all([
        updateProfileSettings(settings),
        updateProfile(profileSubmitData),
      ]);
      getUiBridge().refreshUserProfileData(profileSubmitData.firstName, profileSubmitData.lastName, profileSubmitData.email);
      close();
    } catch (err) {
      const errorMessage = err?.entity?.errorMessage || I18n.t('Failed to save profile');
      
      this.showMessage({
        header: I18n.t('Operation canceled'),
        content: <Flex alignItems="center"><ErrorIcon className="ProfileSettings__error-message-icon" /> {errorMessage}</Flex>,
        size: 'small'
      });
      this.setState((state) => ({ ...state, submitting: false })); 
    }
  }

  get errors() {
    const { profile: { oldPassword, newPassword, newPasswordConfirmed, email } } = this.state;

    return [
      { 
        hasError: !oldPassword && newPassword && newPasswordConfirmed,
        message: 'Please enter current password',
        section: SECTION.profile,
      },
      { 
        hasError: oldPassword && !newPassword,
        message: 'Please enter new password',
        section: SECTION.profile,
      },
      { 
        hasError: newPassword && newPassword !== newPasswordConfirmed,
        message: "Confirmed password doesn't equal to new password",
        section: SECTION.profile,
      },
      { 
        hasError: oldPassword && newPassword && oldPassword === newPassword,
        message: 'The new password must be different from the current password',
        section: SECTION.profile,
      },
      {
        hasError: email && !isEmail(email),
        message: 'Please enter valid email',
        section: SECTION.profile,
      }
    ]
    .filter(({ hasError, section }) => hasError && this.state.currentSection === section)
    .map(({ message }) => message);
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        ...state.messageProps,
        size: 'medium',
        confirmText: 'Ok',
        cancelText: '',
        size: 'small',
        ...props,
      },
    }));
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  showMessage = (config) => {
    this.setMessageProps({
      ...config,
      show: true,
      onCustomOk: this.closeMessage,
    });
  }

  render() {
    const { submitting } = this.state;
    const { close } = this.props;

    return (
      <Modal
        show
        animation={false}
        className="ProfileSettings"
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="ProfileSettings"
        onEscapeKeyDown={close}
        onHide={close}
      >
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...this.state.messageProps}
        />
        <ModalHeader closeButton onHide={close}>
          <ModalTitle id="btn-profile-header">{I18n.t("Profile")}</ModalTitle>
        </ModalHeader>
        {!this.sections && (
          <Flex justify="center" alignItems="center" className="ProfileSettings__content">
            {I18n.t('Loading')}
          </Flex>
        )}
        {this.sections && (
          <>
            <Flex className="ProfileSettings__content">
              <div className="ProfileSettings__content__actions">
                {Object.keys(this.sections).map((sectionName) => {
                  const config = this.sections[sectionName];

                  return (
                    <button
                      id={config.id}
                      className={cx(
                        'ProfileSettings__content__actions__item',
                        { 'ProfileSettings__content__actions__item-selected': sectionName === this.state.currentSection }
                      )}
                      key={sectionName}
                      data-section-name={sectionName}
                      onClick={this.handleChangeSection}
                    >
                      <span className={config.titleIcon} />
                      {config.title}
                    </button>
                  );
                })}
              </div>
              <div className="ProfileSettings__content__section">
                {this.sections[this.state.currentSection].component}
                {this.errors.map((error) => (
                  <h6 key={error} className="ProfileSettings__content__error text-danger">{I18n.t(error)}</h6>
                ))}
              </div>
            </Flex>

            <ModalFooter>
              <Button id="btn-profile-closed" disabled={submitting} onClick={close}>
                {I18n.t('Cancel')}
              </Button>
              <Button
                id="btn-profile-save"
                bsStyle="primary"
                disabled={submitting || Boolean(this.errors.length)}
                onClick={this.handleSave}
              >
                {I18n.t(submitting ? 'Please wait' : 'Save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    );
  }
}